/*global google*/
import React, { useState, useCallback, useEffect } from 'react'
import { GoogleMap } from '@react-google-maps/api'
import { Clusterer } from '@react-google-maps/marker-clusterer'
import {
    MapStyles,
    MapContainerStyles,
    MapCenter,
    getStyleForCluster,
    fitMapToClosestMarker,
} from './utils'
import MapSearchFilter from './MapSearchFilter'
import { PexMapProps } from './types'
import { RecyclingStationItem } from '../Shared/Services/RecyclingStations/interfaces'
import MapInfoWindow from './MapInfoWindow/MapInfoWindow'
import Modal from '../Shared/Components/Modal'
import RecyclingInfoModalContent from './RecyclingInfoModalContent/RecyclingInfoModalContent'
import PexMapClusterer from './PexMapClusterer'

const PexMap = (props: PexMapProps) => {
    const [map, setMap] = useState<google.maps.Map | undefined>(undefined);
    const [clustererInstance, setClustererInstance] = useState<Clusterer>();
    const [showInfoWindow, setShowInfoWindow] = useState<boolean>(false);
    const [showRecyclingOptionsModal, setShowRecyclingOptionsModal] = useState<
        boolean
    >(false);
    const [selectedStation, setSelectedStation] = useState<
        RecyclingStationItem
    >();

    const onLoad = useCallback((mapObj: google.maps.Map) => {
        if (navigator && navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(pos => {
                const coords = pos.coords;
                mapObj.setCenter({
                    lat: coords.latitude,
                    lng: coords.longitude,
                })
                mapObj.setZoom(11);
            })
        }
        setMap(mapObj);
    }, []);

    const onUnmount = useCallback(() => {
        setMap(undefined);
    }, []);

    const onMarkerClick = useCallback(
        (e: google.maps.MapMouseEvent, station: RecyclingStationItem) => {
            const newCenter = new google.maps.LatLng(
                station.lat, 
                station.lng
            );            
            map?.panTo(newCenter);
            setSelectedStation(station);
            setShowInfoWindow(true);
            props.onMarkerClick(station);
        },
        [map]
    );

    const closeInfoWindow = () => {
        setShowInfoWindow(false);
        setSelectedStation(undefined);
    }

    const openRecyclingOptionsModal = () => {
        setShowRecyclingOptionsModal(true);
    }

    const hideRecyclingOptionsModal = () => {
        setShowRecyclingOptionsModal(false);
    }

    const onMapClick = useCallback(() => {
        closeInfoWindow();
    }, [])

    const onSearchSelection = (place: any) => {
        if (place && place.geometry) {
            const geo = place.geometry;
            map?.setCenter({ lat: geo.location.lat(), lng: geo.location.lng() });
            map?.fitBounds(geo.viewport);
            map && fitMapToClosestMarker(props.stationsFiltered, geo.viewport, map);
        }
    }

    const onLoadClusterer = (clusterer: Clusterer) => {
        setClustererInstance(clusterer);
    }

    const clusterCalculator = useCallback((markers: any[]) => {
        const nrOfMarkers = markers.length;
        return {
            text: `${nrOfMarkers}`,
            index: getStyleForCluster(nrOfMarkers),
            title: '',
        }
    }, []);

    useEffect(() => {
        if (clustererInstance && clustererInstance.repaint) {
            clustererInstance.repaint();
        }
    }, [props.stationsFiltered]);

    return (
        <div className='map-wrapper'>
            <MapSearchFilter
                idPrefix={props.idPrefix}
                recyclingOptions={props.recyclingOptions}
                modalLinkText={props.modalLinkText}
                showSearch={props.showSearch}
                defaultSelectedAlternatives={props.defaultSelectedAlternatives}
                openRecyclingOptionsModal={openRecyclingOptionsModal}
                filterStations={props.filterStations}
                onSearchSelection={onSearchSelection}
            />
            <GoogleMap
                id="pexmap-container"
                mapContainerClassName="pexmap-container"
                mapContainerStyle={MapContainerStyles}
                center={MapCenter}
                zoom={6}
                onLoad={onLoad}
                onUnmount={onUnmount}
                onClick={onMapClick}
                options={{
                    fullscreenControl: false,
                    mapTypeControl: false,
                    scaleControl: false,
                    streetViewControl: false,
                    styles: MapStyles,
                }}
            >
                <>
                    {props.stations && props.stations.length > 0 && (
                        <PexMapClusterer
                            calculator={clusterCalculator}
                            onLoad={onLoadClusterer}
                            stations={props.stations}
                            stationsFiltered={props.stationsFiltered}
                            onMarkerClick={onMarkerClick}
                            selectedStation={selectedStation}
                            map={map}
                        />
                    )}
                </>
            </GoogleMap>
            <MapInfoWindow
                idPrefix={props.idPrefix}
                open={showInfoWindow}
                map={map}
                station={selectedStation}
                closeInfoWindow={closeInfoWindow}
                stationDetails={props.stationDetails}
                openRecyclingOptionsModal={openRecyclingOptionsModal}
                loading={props.loadingStationDetails}
            />
            <Modal
                key={`recyclings-options-modal-${showRecyclingOptionsModal}`}
                open={showRecyclingOptionsModal}
                handleClose={hideRecyclingOptionsModal}
            >
                <RecyclingInfoModalContent
                    cmsContent={props.recycleDialogContent.Html}
                />
            </Modal>
        </div>
    )
}

export default PexMap